// Controller UI del costruttore di costi deducibili (/piva-ordinaria).
// Sul modello di js/addizionaliComunali.js: stato privato, un init piu' pochi
// getter, alimenta un campo del form principale (#costi). Le regole vengono
// da data/costi-deducibili.json e il calcolo da js/costiDeducibiliCalc.js:
// qui c'e' solo DOM.
//
// Le righe delle categorie sono generate dal catalogo dentro il modale, con
// regola e note visibili sotto ogni voce (niente tooltip: js/tooltips.js
// registra i listener solo al DOMContentLoaded e non funzionerebbe su markup
// generato). Invariante di serializzazione: il dettaglio (getCostiDettaglio)
// esiste solo finche' #costi coincide col totale dedotto ("collegato"); una
// modifica manuale del campo scollega il dettaglio.
import { calcolaCostiDeducibili, tipoContribuente } from '/js/costiDeducibiliCalc.js';
import catalogo from '/data/costi-deducibili.json';
import {
    trackBuilderOpen,
    trackBuilderApply,
    trackBuilderDetach
} from '/js/costiDeducibiliAnalytics.js';

const euro = (value) => `${Math.round(value).toLocaleString('it-IT')} €`;

// Articoli corretti davanti ai numeri: "all'80%", "al 20%", "l'1%", "il 2%".
const alPct = (pct) => (/^(1$|8|11)/.test(String(pct)) ? `all'${pct}%` : `al ${pct}%`);
const conArticolo = (label) => (/^1(?!\d)/.test(label) ? `l'${label}` : `il ${label}`);

let opzioni = null;            // { costiElement, getContesto, onApply }
let importi = new Map();       // categoriaId -> importo sostenuto (anche righe nascoste)
let collegato = false;         // #costi coincide col totale dedotto corrente
let usato = false;             // il costruttore e' stato applicato almeno una volta
let ultimoEsito = null;

const el = (id) => document.getElementById(id);

// --- Descrizione della regola sotto ogni voce ------------------------------
const descriviRegola = (cat, tipo) => {
    const regola = cat.regole[tipo];
    if (!regola) return '';
    const parti = [];
    if (regola.pct === 0) {
        parti.push('Non deducibile');
    } else {
        let base = `Deducibile ${alPct(regola.pct)}`;
        if (Number.isFinite(regola.tetto_base)) base += ` su massimo ${euro(regola.tetto_base)}`;
        if (regola.plafond) {
            const plafond = catalogo.plafond[regola.plafond];
            if (plafond?.label) base += `, entro ${conArticolo(plafond.label)}`;
        }
        parti.push(base);
    }
    const quotaIva = cat.iva
        ? (typeof cat.iva.quota_detraibile_pct === 'object'
            ? cat.iva.quota_detraibile_pct[tipo]
            : cat.iva.quota_detraibile_pct)
        : null;
    if (Number.isFinite(quotaIva) && quotaIva > 0) parti.push(`IVA detraibile ${quotaIva}%`);
    if (cat.norma) parti.push(cat.norma);
    return parti.join(' · ');
};

// --- Costruzione del corpo del modale --------------------------------------
const costruisciCorpo = () => {
    const corpo = el('costiBuilderBody');
    if (!corpo) return;
    const frammento = document.createDocumentFragment();
    for (const gruppo of catalogo.gruppi) {
        const categorie = catalogo.categorie.filter((c) => c.gruppo === gruppo.id);
        if (categorie.length === 0) continue;
        const intestazione = document.createElement('h3');
        intestazione.className = 'costi-builder__gruppo';
        intestazione.innerHTML = `<i class="ph ${gruppo.icona}" aria-hidden="true"></i> ${gruppo.label}`;
        frammento.appendChild(intestazione);
        for (const cat of categorie) {
            const rigaEl = document.createElement('div');
            rigaEl.className = 'costi-builder__row';
            rigaEl.dataset.categoria = cat.id;
            rigaEl.innerHTML = `
                <div class="costi-builder__info">
                    <label for="cb-${cat.id}">${cat.label}</label>
                    <p class="costi-builder__regola" data-regola></p>
                    ${cat.hint ? `<p class="costi-builder__hint">${cat.hint}</p>` : ''}
                    ${cat.nota ? `<p class="costi-builder__nota">${cat.nota}</p>` : ''}
                    ${cat.avvertenza ? `<p class="costi-builder__avvertenza"><i class="ph ph-warning" aria-hidden="true"></i> ${cat.avvertenza}</p>` : ''}
                </div>
                <div class="costi-builder__importo">
                    <input type="number" id="cb-${cat.id}" min="0" step="50" inputmode="decimal"
                           placeholder="€ annui" aria-label="Importo annuo: ${cat.label}">
                    <span class="costi-builder__dedotto" data-dedotto hidden></span>
                    <span class="costi-builder__badge" data-badge hidden></span>
                </div>`;
            frammento.appendChild(rigaEl);
        }
    }
    corpo.appendChild(frammento);
    corpo.addEventListener('input', (event) => {
        const input = event.target.closest('input[type="number"]');
        if (!input) return;
        const id = input.id.replace(/^cb-/, '');
        const valore = Math.max(0, parseFloat(input.value) || 0);
        if (valore > 0) importi.set(id, valore); else importi.delete(id);
        ricalcola();
    });
};

// --- Ricalcolo e rendering -------------------------------------------------
const contesto = () => {
    const { ricavi, formaAttivita, cassaId } = opzioni.getContesto();
    return {
        ricavi,
        tipo: tipoContribuente(formaAttivita, cassaId, catalogo) || 'professionista'
    };
};

const vociVisibili = (tipo) => {
    const voci = [];
    for (const cat of catalogo.categorie) {
        if (Array.isArray(cat.solo) && !cat.solo.includes(tipo)) continue;
        const importo = importi.get(cat.id) || 0;
        if (importo > 0) voci.push({ categoriaId: cat.id, importo });
    }
    return voci;
};

const ricalcola = () => {
    if (!opzioni) return null;
    const { ricavi, tipo } = contesto();
    const esito = calcolaCostiDeducibili({ voci: vociVisibili(tipo), ricavi, tipo }, catalogo);
    ultimoEsito = esito;

    for (const cat of catalogo.categorie) {
        const rigaEl = document.querySelector(`.costi-builder__row[data-categoria="${cat.id}"]`);
        if (!rigaEl) continue;
        const nascosta = Array.isArray(cat.solo) && !cat.solo.includes(tipo);
        rigaEl.hidden = nascosta;
        if (nascosta) continue;
        rigaEl.querySelector('[data-regola]').textContent = descriviRegola(cat, tipo);

        const rigaEsito = esito.righe.find((r) => r.categoriaId === cat.id);
        const dedottoEl = rigaEl.querySelector('[data-dedotto]');
        const badgeEl = rigaEl.querySelector('[data-badge]');
        if (!rigaEsito) {
            dedottoEl.hidden = true;
            badgeEl.hidden = true;
            continue;
        }
        dedottoEl.hidden = false;
        dedottoEl.textContent = `→ deduci ${euro(rigaEsito.dedotto)}`;
        let badge = '';
        if (rigaEsito.limiteApplicato === 'indeducibile') {
            badge = 'non deducibile';
        } else if (rigaEsito.limiteApplicato === 'plafond') {
            badge = ricavi > 0
                ? `oltre ${conArticolo(catalogo.plafond[rigaEsito.dettaglio.plafondId]?.label || 'limite')}`
                : 'il limite dipende dai ricavi: compila prima il campo Ricavi';
        } else if (rigaEsito.limiteApplicato === 'tetto') {
            badge = `tetto ${euro(rigaEsito.dettaglio.tettoBase)}`;
        }
        badgeEl.hidden = !badge;
        badgeEl.textContent = badge;
    }

    el('costiBuilderTotSostenuto').textContent = euro(esito.totaleSostenuto);
    el('costiBuilderTotDedotto').textContent = euro(esito.totaleDedotto);
    el('costiBuilderTotIva').textContent = `~${euro(esito.totaleIvaStimata)}`;
    const applica = el('costiBuilderApply');
    if (applica) applica.disabled = esito.righe.length === 0;
    return esito;
};

// --- Recap sotto il campo costi --------------------------------------------
const aggiornaRecap = () => {
    const recap = el('costiBuilderRecap');
    if (!recap) return;
    if (!usato) {
        recap.hidden = true;
        return;
    }
    recap.hidden = false;
    if (collegato && ultimoEsito) {
        const n = ultimoEsito.righe.length;
        recap.className = 'costi-builder__recap';
        recap.innerHTML = `<strong>${euro(ultimoEsito.totaleDedotto)}</strong> dedotti su ` +
            `${euro(ultimoEsito.totaleSostenuto)} sostenuti · ${n} ${n === 1 ? 'categoria' : 'categorie'} ` +
            `<button type="button" class="costi-builder__link" data-costi-open>Modifica</button>`;
    } else {
        recap.className = 'costi-builder__recap costi-builder__recap--staccato';
        recap.innerHTML = 'Totale modificato a mano: il dettaglio per categoria non è più allineato. ' +
            '<button type="button" class="costi-builder__link" data-costi-open>Riapri il costruttore</button>';
    }
};

// --- Apertura / chiusura ----------------------------------------------------
const apri = () => {
    const modal = el('costiBuilderModal');
    if (!modal) return;
    ricalcola();
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('modal-open');
    trackBuilderOpen();
    const primoInput = modal.querySelector('.costi-builder__row:not([hidden]) input');
    window.setTimeout(() => primoInput?.focus(), 50);
};

const chiudi = () => {
    const modal = el('costiBuilderModal');
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('modal-open');
};

const applica = () => {
    const esito = ricalcola();
    if (!esito) return;
    const totale = Math.round(esito.totaleDedotto);
    opzioni.costiElement.value = totale;
    collegato = true;
    usato = true;
    aggiornaRecap();
    chiudi();
    trackBuilderApply({
        categorie: esito.righe.length,
        conTetto: esito.righe.filter((r) => r.limiteApplicato === 'tetto').length,
        conPlafond: esito.righe.filter((r) => r.limiteApplicato === 'plafond').length
    });
    // change (non input): il listener di scollegamento ascolta solo input,
    // cosi' la scrittura programmatica non si auto-scollega.
    opzioni.costiElement.dispatchEvent(new Event('change', { bubbles: true }));
    opzioni.onApply?.(totale);
};

// --- API pubblica -----------------------------------------------------------
// opts: { costiElement, getContesto: () => ({ ricavi, formaAttivita, cassaId }), onApply? }
export function initCostiBuilder(opts) {
    opzioni = opts;
    costruisciCorpo();

    document.querySelectorAll('[data-costi-open]').forEach((btn) => btn.addEventListener('click', apri));
    // Il recap rigenera il bottone "Modifica": delega sul contenitore.
    el('costiBuilderRecap')?.addEventListener('click', (event) => {
        if (event.target.closest('[data-costi-open]')) apri();
    });
    const modal = el('costiBuilderModal');
    modal?.addEventListener('click', (event) => {
        if (event.target.matches('[data-costi-close]')) chiudi();
    });
    modal?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') chiudi();
    });
    el('costiBuilderApply')?.addEventListener('click', applica);

    // Modifica manuale di #costi: il dettaglio non e' piu' il totale, scollega.
    opts.costiElement?.addEventListener('input', () => {
        if (!usato || !collegato) return;
        collegato = false;
        aggiornaRecap();
        trackBuilderDetach();
    });
}

// Da chiamare quando cambiano ricavi, forma o cassa: i plafond e le righe
// visibili dipendono dal contesto. Se il totale collegato cambia (es. il
// plafond del vitto si muove coi ricavi), riallinea #costi.
export function aggiornaContestoCosti() {
    if (!opzioni) return;
    const esito = ricalcola();
    if (collegato && esito) {
        const totale = Math.round(esito.totaleDedotto);
        if (parseFloat(opzioni.costiElement.value) !== totale) {
            opzioni.costiElement.value = totale;
            opzioni.costiElement.dispatchEvent(new Event('change', { bubbles: true }));
        }
        aggiornaRecap();
    }
}

// Dettaglio compatto per il payload URL: [[categoriaId, importo], ...] solo se
// collegato (invariante: cd presente => #costi === totale dedotto).
export function getCostiDettaglio() {
    if (!collegato || !ultimoEsito || ultimoEsito.righe.length === 0) return null;
    return ultimoEsito.righe.map((r) => [r.categoriaId, Math.round(r.sostenuto)]);
}

// Ripristino dal payload (?d=). Ricollega solo se il totale ricalcolato
// coincide con params.costi: catalogo cambiato o payload manipolato lasciano
// fede al totale, scollegato.
export function applicaDettaglio(cd, costiTotale) {
    if (!opzioni || !Array.isArray(cd) || cd.length === 0) return false;
    const validi = new Set(catalogo.categorie.map((c) => c.id));
    importi = new Map();
    for (const coppia of cd) {
        if (!Array.isArray(coppia)) continue;
        const [id, importo] = coppia;
        if (!validi.has(id)) {
            console.warn(`Costruttore costi: categoria sconosciuta nel link, ignorata: ${id}`);
            continue;
        }
        const valore = Math.max(0, parseFloat(importo) || 0);
        if (valore > 0) importi.set(id, valore);
    }
    for (const [id, valore] of importi) {
        const input = el(`cb-${id}`);
        if (input) input.value = valore;
    }
    const esito = ricalcola();
    usato = importi.size > 0;
    collegato = Boolean(esito) && Math.round(esito.totaleDedotto) === Math.round(costiTotale || 0);
    aggiornaRecap();
    return collegato;
}
